/**
 * BarChart
 *
 * Inline-SVG horizontal bar chart. No chart library per CONSTITUTION.
 */

interface BarChartRow {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  rows: BarChartRow[];
  maxWidth?: number;
  barHeight?: number;
  barGap?: number;
  valueFormatter?: (v: number) => string;
}

const DEFAULT_COLOR = '#7c3aed';

export function BarChart({
  rows,
  maxWidth = 300,
  barHeight = 18,
  barGap = 8,
  valueFormatter = (v) => v.toLocaleString(),
}: BarChartProps) {
  if (rows.length === 0) {
    return <p className="text-xs text-zinc-500 italic">No data</p>;
  }

  const maxVal = Math.max(...rows.map((r) => r.value), 1);
  const labelWidth = 120;
  const valueWidth = 60;
  const barAreaWidth = maxWidth - labelWidth - valueWidth - 8;
  const svgHeight = rows.length * (barHeight + barGap) - barGap;

  return (
    <svg
      width={maxWidth}
      height={svgHeight}
      viewBox={`0 0 ${maxWidth} ${svgHeight}`}
      role="img"
      aria-label={rows.map((r) => `${r.label}: ${valueFormatter(r.value)}`).join(', ')}
    >
      {rows.map((row, i) => {
        const y = i * (barHeight + barGap);
        const barWidth = (row.value / maxVal) * barAreaWidth;

        return (
          <g key={row.label}>
            <text
              x={labelWidth - 6}
              y={y + barHeight / 2}
              textAnchor="end"
              dominantBaseline="middle"
              fill="#a1a1aa"
              fontSize="11"
            >
              {row.label.length > 16 ? row.label.slice(0, 15) + '…' : row.label}
            </text>
            <rect
              x={labelWidth}
              y={y}
              width={Math.max(barWidth, 2)}
              height={barHeight}
              fill={row.color || DEFAULT_COLOR}
              rx="2"
            />
            <text
              x={labelWidth + Math.max(barWidth, 2) + 6}
              y={y + barHeight / 2}
              dominantBaseline="middle"
              fill="#a1a1aa"
              fontSize="11"
            >
              {valueFormatter(row.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
